import React, { useEffect, useState } from 'react';
import { connectorWrapper } from '../search/algoliaConnectors';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { styles } from './SponseredContentResults.css';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { AlgoliaProductHit } from '../../utils/types';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getStoreCurrency } from '../../utils/shopify/getStoreCurrency';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { GIFTCARDSKU } from '../../utils/gift-card.js';
import { useInstantSearch } from 'react-instantsearch';
import { postAlgoliaProductClick, postAlgoliaProductView } from '../../utils/algolia/search-insights';

const SponsoredContentResult = connectorWrapper(
  useInstantSearch,
  ({ results: searchResults, storedProducts }: { results: any; storedProducts: Array<string> }) => {
    const isDesktop = useBreakpoint('lg', 'up');
    const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
    const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
    const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();

    const sortHeroProducts = (a: AlgoliaProductHit, b: AlgoliaProductHit) => {
      const getProductSku = (product: AlgoliaProductHit, itemId: string) =>
        product.sku === itemId.toString() || product.skus?.includes(itemId.toString());
      return (
        storedProducts.findIndex(itemId => getProductSku(a, itemId)) -
        storedProducts.findIndex(itemId => getProductSku(b, itemId))
      );
    };

    const sortedHeroProducts = searchResults ? [...searchResults.hits].sort(sortHeroProducts) : [];
    const [carouselTracked, setCarouselTracked] = useState(false);

    useEffect(() => {
      if (sortedHeroProducts.length && !carouselTracked) {
        trackViewItemList({
          currency: getStoreCurrency(),
          listName: 'Collection - Hero Content',
          viewedItems: transformAlgoliaHitForTracking(sortedHeroProducts),
        });
        postAlgoliaProductView(sortedHeroProducts);
        setCarouselTracked(true);
      }
    }, [sortedHeroProducts]);

    if (sortedHeroProducts.length) {
      return (
        <div className={styles.heroContentContainer}>
          <div className={styles.heroContentInnerContainer}>
            <DomFont as="h3" className={styles.heroContentTitle}>
              Featured
            </DomFont>

            <div className={styles.heroContentHits}>
              {isDesktop ? (
                <div className={styles.productCardContainer}>
                  {sortedHeroProducts.map((hit, i) => (
                    <ProductCard
                      {...AlgoliaHitTransformer.toProductCardProps(hit, {
                        trackingDescriptor: {
                          page: 'collection',
                          group: 'heroContent',
                          currency: getStoreCurrency(),
                          ecommerceExtraProps: {
                            list: 'collection',
                            position: i + 1,
                          },
                        },
                      })}
                      {...productCartPropsOverrides}
                      key={`${hit.variant_id} ${i}`}
                      shouldHideSwatches
                      variant="tablet"
                      getIsCompared={isAddedToCompare}
                      onCompare={compareHandleClick}
                      getShouldShowCompare={shouldShowCompare}
                      isLoggedIn={isLoggedIn}
                      shouldHideCartOverlay={productCartPropsOverrides.shouldHideCartOverlay || GIFTCARDSKU === hit.sku}
                      onUpdateWishlist={handleUpdateWishlist}
                      getIsInWishlist={getIsInWishlist}
                      onClick={() => {
                        postAlgoliaProductClick(hit.sku, i);
                      }}
                      onAddToCart={
                        onAddToCart
                          ? async (e: any, sku: any, variantId: any, eventData: any) => {
                              const isSuccess = await onAddToCart(e, hit, variantId, eventData?.trackingDescriptor);
                              return isSuccess;
                            }
                          : undefined
                      }
                    />
                  ))}
                </div>
              ) : (
                <ProductCarousel
                  fullBleed={false}
                  fullHeightItems
                  products={sortedHeroProducts}
                  renderProduct={({ hit, position }) => (
                    <ProductCard
                      {...AlgoliaHitTransformer.toProductCardProps(hit, {
                        trackingDescriptor: {
                          page: 'collection',
                          group: 'heroContent',
                          currency: getStoreCurrency(),
                          ecommerceExtraProps: {
                            list: 'collection - heroTile',
                            position,
                          },
                        },
                      })}
                      {...productCartPropsOverrides}
                      shouldHideSwatches
                      variant="tablet"
                      getIsCompared={isAddedToCompare}
                      onCompare={compareHandleClick}
                      getShouldShowCompare={shouldShowCompare}
                      isLoggedIn={isLoggedIn}
                      onUpdateWishlist={handleUpdateWishlist}
                      getIsInWishlist={getIsInWishlist}
                      onAddToCart={
                        onAddToCart
                          ? (e, _sku, variantId, eventData) =>
                              onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                          : undefined
                      }
                    />
                  )}
                />
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }
);

export default SponsoredContentResult;
